/** One entry of a listbox, reduced to what keyboard navigation needs. */
export interface NavOption {
  /** Matched against the control's `value`; must be unique in the list. */
  value: string
  /** Plain-text form of the label, matched by typeahead. */
  text: string
  disabled?: boolean
}

/**
 * Where the active option starts when the list opens: the selected one, else the first enabled one,
 * else the top. A `value` that matches nothing (a stale id) deliberately falls back rather than
 * throwing, and `''` matches an option whose value is `''` (the "—" / "default" sentinel rows).
 */
export function initialIndex(options: NavOption[], value: string): number {
  const match = options.findIndex(o => o.value === value)
  if (match >= 0) return match
  const first = options.findIndex(o => !o.disabled)
  return first >= 0 ? first : 0
}

/** Move `active` by `delta` (±1) steps, skipping disabled options and wrapping. */
export function moveIndex(options: NavOption[], active: number, delta: number): number {
  const n = options.length
  if (n === 0) return 0
  let i = active
  for (let step = 0; step < n; step++) {
    i = (i + delta + n) % n
    if (!options[i].disabled) return i
  }
  return active
}

/** First (`'first'`) or last (`'last'`) enabled index; `-1` when every option is disabled. */
export function edgeIndex(options: NavOption[], edge: 'first' | 'last'): number {
  if (edge === 'first') return options.findIndex(o => !o.disabled)
  for (let i = options.length - 1; i >= 0; i--) if (!options[i].disabled) return i
  return -1
}

/**
 * Collapse a buffer made of one repeated character ("aaa" → "a") so pressing the same key cycles
 * through the options starting with it, the way a native select does.
 */
export function typeaheadQuery(buffer: string): string {
  return buffer.length > 1 && /^(.)\1*$/.test(buffer) ? buffer[0] : buffer
}

/**
 * First enabled option at or after `from` (wrapping) whose text starts with `query`,
 * case-insensitively; `-1` when nothing matches.
 */
export function typeaheadIndex(options: NavOption[], query: string, from: number): number {
  const q = query.toLowerCase()
  if (!q || options.length === 0) return -1
  const start = from < 0 ? 0 : from
  for (let step = 0; step < options.length; step++) {
    const i = (start + step) % options.length
    const o = options[i]
    if (!o.disabled && o.text.toLowerCase().startsWith(q)) return i
  }
  return -1
}
